var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Album } from 'album/classes/Album.class';
import { AlbumPhoto } from 'album/classes/AlbumPhoto.class';
import axiosInstance from '../../store/AxiosSingleton';
import { showSnackbar, SnackbarType } from 'common/store/actions';
import { dispatch } from 'store/store';
export class AlbumService {
    /**
     * Sauvegarde d'un album
     * @param album Album à sauvegarder
     * @return Identifiant de l'album sauvegardé
     */
    static saveAlbum(album) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { content }, } = yield axiosInstance.post('/index.php', {
                album: album.toRaw(),
            }, {
                params: {
                    rub: AlbumService.rubModule,
                    action: 'SAVE_ALBUM',
                },
            });
            return parseInt(content, 10);
        });
    }
    /**
     * Suppression d'un album
     * @param idAlbum Identifiant de l'album à supprimer
     * @return true si l'album a été supprimé
     */
    static deleteAlbum(idAlbum, idRaison) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { content }, } = yield axiosInstance.delete('/index.php', {
                params: {
                    rub: AlbumService.rubModule,
                    action: 'DELETE_ALBUM',
                    idAlbum,
                    idRaison,
                },
            });
            return content;
        });
    }
    /**
     * Sauvegarde les images d'un album
     * @param images Images que l'on veut ajouter à l'album
     * @return true si les images ont été sauvegardées
     */
    static saveAlbumImages(images) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!images) {
                return null;
            }
            const { data: { content }, } = yield axiosInstance.post('/index.php', {
                albumImages: images.map((albumPhoto) => albumPhoto.toRaw()),
            }, {
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    dispatch(showSnackbar({
                        message: 'Importation des photos en cours',
                        type: SnackbarType.INFO,
                        progress,
                        button: null,
                    }));
                },
                params: {
                    rub: AlbumService.rubModule,
                    action: 'SAVE_ALBUM_IMAGES',
                },
            });
            return content === null || content === void 0 ? void 0 : content.map((albumPhoto) => new AlbumPhoto(albumPhoto));
        });
    }
    /**
     * Valide des photos dans un album
     * @param idAlbum Identifiant de l'album
     * @param idAlbumPhoto Liste des identifiants des photos à valider
     * @return Nombre de photos validées
     */
    static validateAlbumPhotos(idAlbum, idAlbumPhotos) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { content }, } = yield axiosInstance.post('/index.php', { idAlbum, idAlbumPhotos }, {
                params: {
                    rub: AlbumService.rubModule,
                    action: 'VALIDATE_ALBUM_PHOTOS',
                },
            });
            return content;
        });
    }
    /**
     * Supprime des photos dans un album
     * @param idAlbumPhoto Identifiants des photos de l'album que l'on souhaite supprimer
     * @return Nombre de photos supprimées
     */
    static deletePhotosFromAlbum(idAlbumPhotos) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { content }, } = yield axiosInstance.delete('/index.php', {
                params: {
                    rub: AlbumService.rubModule,
                    action: 'DELETE_ALBUM_PHOTOS',
                    idAlbumPhotos,
                },
            });
            return content;
        });
    }
    /**
     * Charge les photos d'un album
     * @param idAlbum Identifiant de l'album
     * @return Liste des photos de l'album
     */
    static loadPhotosToValidate(idAlbum) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { content }, } = yield axiosInstance.get('/index.php', {
                params: {
                    rub: AlbumService.rubModule,
                    action: 'LOAD_ALBUM_PHOTOS',
                    idAlbum,
                },
            });
            return content.map((photo) => new AlbumPhoto(photo));
        });
    }
    /**
     * Charges des albums
     * @param request Paramètres de la requête
     * @return Liste des albums
     */
    static loadAlbums(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { content }, } = yield axiosInstance.get('/index.php', {
                params: Object.assign({ rub: AlbumService.rubModule, action: 'LOAD_ALBUMS' }, request),
            });
            return {
                albums: content.albums.map((album) => new Album(album)),
                total: content.total,
            };
        });
    }
    /**
     * Charges un album à partir de son id
     * @param idAlbum Identifiant de l'album
     * @return Album trouvé
     */
    static loadAlbumFromId(idAlbum) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { content }, } = yield axiosInstance.get('/index.php', {
                params: {
                    rub: AlbumService.rubModule,
                    action: 'LOAD_ALBUM_FROM_ID',
                    idAlbum,
                },
            });
            return content === null ? null : new Album(content);
        });
    }
    /**
     * Charges les albums suggérés à partir d'un album
     * @param idAlbum Identifiant de l'album
     * @return Liste des albums suggérés
     */
    static loadRelativeAlbums(idAlbum) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { content }, } = yield axiosInstance.get('/index.php', {
                params: {
                    rub: AlbumService.rubModule,
                    action: 'LOAD_RELATIVE_ALBUMS',
                    idAlbum,
                },
            });
            return content.map((album) => new Album(album));
        });
    }
    /**
     * Charges des albums photos en appliquant des filtres
     * @param request Paramètres de la requête
     * @return Liste des albums
     */
    static loadAlbumsFromMember(request) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { content }, } = yield axiosInstance.get('/index.php', {
                params: Object.assign({ rub: AlbumService.rubModule, action: 'LOAD_ALBUMS_FROM_MEMBER' }, request),
            });
            return {
                albums: content.albums.map((album) => new Album(album)),
                total: content.total,
            };
        });
    }
    /**
     * Charge le nombre d'albums non-modérés
     * @return Nombre d'albums non-modérés
     */
    static loadUnmoderatedAlbumAmount() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data: { content }, } = yield axiosInstance.get('/index.php', {
                params: {
                    rub: AlbumService.rubModule,
                    action: 'LOAD_UNMODERATED_ALBUM_AMOUNT',
                },
            });
            return content;
        });
    }
}
AlbumService.rubModule = 'album';
//# sourceMappingURL=data:application/json;base64,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